import {
  dep,
  ManifestParseError,
  type ManifestParser,
  type ParsedManifest,
  rangeDep,
  type SkippedDependency,
} from './types';

/**
 * Parser for Maven `pom.xml` files.
 *
 * No XML library is available in the test environment (pure Node, no DOMParser).
 * All scanning is done with regex and string manipulation.
 *
 * v1 policy:
 *   - Literal Maven versions (1.2.3, 2.0.0-M1, 1.2.3.RELEASE, etc.) → exact.
 *   - `${property}` → resolved from <properties>, then re-evaluated.
 *   - `${project.version}` → resolved from the top-level <version>.
 *   - Unresolvable property → skipped ('unresolved property').
 *   - Range syntax `[1,2)` / `(,1.0]` → emitted as a range for the Worker to
 *     resolve to its upper bound.
 *   - Missing <version> in <dependencies> but present in <dependencyManagement>
 *     → use that version (BOM-managed, but the version is visible in the POM).
 *   - Missing <version> with no fallback → skipped ('version managed elsewhere').
 *   - <type>pom</type> with <scope>import</scope> in dependencyManagement → skip.
 *
 * name = `groupId:artifactId` (deps.dev MAVEN canonical form).
 */

// ─── Utility helpers ──────────────────────────────────────────────────────────

/** Strip XML comments (`<!-- ... -->`) from the input, including multiline. */
function stripXmlComments(xml: string): string {
  return xml.replace(/<!--[\s\S]*?-->/g, '');
}

/** Normalise CRLF → LF. */
function normalise(xml: string): string {
  return xml.replace(/\r\n/g, '\n');
}

/**
 * Extract the text content of the FIRST occurrence of `<tag>…</tag>` inside
 * `scope`. Returns `null` when not found.
 */
function innerText(scope: string, tag: string): string | null {
  const re = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'i');
  const m = scope.match(re);
  // m[1] is always defined when m is truthy (group 1 exists in the pattern).
  return m ? (m[1] ?? '').trim() : null;
}

/**
 * Collect all non-overlapping substrings that match `<tag>…</tag>` within
 * `scope`. Simple (not namespace-aware) — sufficient for pom.xml conventions.
 */
function collectBlocks(scope: string, tag: string): string[] {
  const re = new RegExp(`<${tag}[^>]*>([\\s\\S]*?)<\\/${tag}>`, 'gi');
  const blocks: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(scope)) !== null) {
    blocks.push(m[0]);
  }
  return blocks;
}

// ─── Properties resolution ────────────────────────────────────────────────────

/** Collect `<properties>` entries as a key→value map. */
function extractProperties(xml: string): Map<string, string> {
  const props = new Map<string, string>();

  // There may be multiple <properties> blocks (unusual but valid).
  const propBlocks = collectBlocks(xml, 'properties');
  for (const block of propBlocks) {
    // Extract every <someKey>value</someKey> child inside the block content.
    // We strip the outer <properties> tag to only scan children.
    const inner = block.replace(/^<properties[^>]*>/i, '').replace(/<\/properties>$/i, '');
    const childRe = /<([A-Za-z0-9._-]+)[^>]*>([\s\S]*?)<\/\1>/g;
    let m: RegExpExecArray | null;
    while ((m = childRe.exec(inner)) !== null) {
      // m[1] and m[2] are always present when m is truthy.
      if (m[1] !== undefined && m[2] !== undefined) {
        props.set(m[1], m[2].trim());
      }
    }
  }

  return props;
}

/** Resolve `${prop}` placeholders up to 5 levels deep (avoids cycles). */
function resolveProperty(
  value: string,
  props: Map<string, string>,
  projectVersion: string | null,
  depth = 0,
): string | null {
  if (depth > 5) return null; // cycle guard

  const m = value.match(/^\$\{([^}]+)\}$/);
  if (!m) return value; // not a placeholder — already literal

  const key = m[1] ?? '';

  if (key === 'project.version' && projectVersion !== null) {
    return resolveProperty(projectVersion, props, projectVersion, depth + 1);
  }

  // Also handle `${project.parent.version}` etc. as unresolvable for simplicity.
  if (key.startsWith('project.') && key !== 'project.version') {
    return null;
  }

  const resolved = props.get(key);
  if (resolved === undefined) return null; // unresolvable

  return resolveProperty(resolved, props, projectVersion, depth + 1);
}

// ─── Version classification ───────────────────────────────────────────────────

/** Maven version range characters: brackets/parens signal a range. */
const RANGE_RE = /^[[(]/;

/** Literal Maven version: dotted/alnum segments, may include `-` or `.` qualifiers. */
const LITERAL_VERSION_RE = /^[0-9][A-Za-z0-9._-]*$/;

type VersionResult =
  | { kind: 'exact'; version: string }
  /** A Maven interval (`[1.0,2.0)`) — resolvable to its upper bound. */
  | { kind: 'range' }
  /** Neither literal nor interval (e.g. an embedded `${...}`). */
  | { kind: 'unparseable' }
  | { kind: 'property'; key: string }
  | { kind: 'missing' };

function classifyVersion(raw: string | null): VersionResult {
  if (raw === null || raw.trim() === '') return { kind: 'missing' };

  const v = raw.trim();

  if (RANGE_RE.test(v)) return { kind: 'range' };

  if (/^\$\{([^}]+)\}$/.test(v)) {
    return { kind: 'property', key: v.slice(2, -1) };
  }

  if (LITERAL_VERSION_RE.test(v)) return { kind: 'exact', version: v };

  // Anything else — e.g. a version with an embedded ${...} in the middle.
  return { kind: 'unparseable' };
}

// ─── Dependency block parsing ─────────────────────────────────────────────────

interface RawDep {
  groupId: string;
  artifactId: string;
  version: string | null;
  scope: string | null;
  type: string | null;
}

function parseDependencyBlock(block: string): RawDep | null {
  // Extract inner content of the <dependency> element.
  const inner = block.replace(/^<dependency[^>]*>/i, '').replace(/<\/dependency>$/i, '');

  const groupId = innerText(inner, 'groupId');
  const artifactId = innerText(inner, 'artifactId');

  if (!groupId || !artifactId) return null;

  return {
    groupId,
    artifactId,
    version: innerText(inner, 'version'),
    scope: innerText(inner, 'scope'),
    type: innerText(inner, 'type'),
  };
}

// ─── Main parser ──────────────────────────────────────────────────────────────

/**
 * Parses a Maven `pom.xml` manifest.
 *
 * Throws `ManifestParseError` when the input contains no `<dependency` block
 * AND no `<project` tag — i.e. it is clearly not a pom.xml.
 */
export const parsePomXml: ManifestParser = (content: string): ParsedManifest => {
  const xml = stripXmlComments(normalise(content));

  // Sanity check: must look like some kind of XML/Maven file.
  const hasProject = /<project[\s>]/i.test(xml);
  const hasDependency = /<dependency[\s>]/i.test(xml);
  if (!hasProject && !hasDependency) {
    throw new ManifestParseError('Input does not appear to be a pom.xml file');
  }

  // ── Project-level version ──────────────────────────────────────────────────
  // The project <version> sits as a direct child of <project>, not inside a
  // deeper nested element. We extract it from the leading portion of the file
  // to avoid picking up a nested module's version.
  let projectVersion: string | null = null;
  const projectVersionMatch = xml.match(/<project[\s\S]*?<version[^>]*>([\s\S]*?)<\/version>/i);
  if (projectVersionMatch) {
    projectVersion = (projectVersionMatch[1] ?? '').trim() || null;
  }

  // ── Properties ────────────────────────────────────────────────────────────
  const props = extractProperties(xml);

  // ── Collect dependencyManagement <dependency> blocks ──────────────────────
  // We extract the content of <dependencyManagement> sections first so we can
  // fall back to them for missing versions.
  const mgmtMap = new Map<string, string>(); // key → version string

  const mgmtSectionRe = /<dependencyManagement[^>]*>([\s\S]*?)<\/dependencyManagement>/gi;
  let mgmtMatch: RegExpExecArray | null;
  while ((mgmtMatch = mgmtSectionRe.exec(xml)) !== null) {
    const mgmtContent = mgmtMatch[1] ?? '';
    const mgmtDeps = collectBlocks(mgmtContent, 'dependency');
    for (const block of mgmtDeps) {
      const raw = parseDependencyBlock(block);
      if (!raw) continue;

      // Skip BOM imports (<type>pom</type> <scope>import</scope>).
      if (raw.type?.toLowerCase() === 'pom' && raw.scope?.toLowerCase() === 'import') continue;

      const key = `${raw.groupId}:${raw.artifactId}`;
      if (!mgmtMap.has(key) && raw.version !== null) {
        mgmtMap.set(key, raw.version);
      }
    }
  }

  // ── Collect direct <dependencies> blocks ──────────────────────────────────
  // We need to scan <dependencies> sections that are NOT inside
  // <dependencyManagement>. Strategy: remove all <dependencyManagement> sections
  // from the text before scanning for remaining <dependency> blocks.
  const xmlWithoutMgmt = xml.replace(
    /<dependencyManagement[^>]*>[\s\S]*?<\/dependencyManagement>/gi,
    '',
  );
  const directDepBlocks = collectBlocks(xmlWithoutMgmt, 'dependency');

  // ── Process direct deps ───────────────────────────────────────────────────
  const dependencies = [];
  const skipped: SkippedDependency[] = [];
  const seen = new Set<string>(); // by `groupId:artifactId`

  for (const block of directDepBlocks) {
    const raw = parseDependencyBlock(block);
    if (!raw) continue;

    const key = `${raw.groupId}:${raw.artifactId}`;
    if (seen.has(key)) continue; // first occurrence wins
    seen.add(key);

    // Resolve version: direct block first, then dependencyManagement fallback.
    let versionRaw = raw.version;
    if (versionRaw === null && mgmtMap.has(key)) {
      versionRaw = mgmtMap.get(key)!;
    }

    const classified = classifyVersion(versionRaw ?? null);

    switch (classified.kind) {
      case 'exact':
        dependencies.push(dep('MAVEN', key, classified.version, null));
        break;

      case 'property': {
        const resolved = resolveProperty(`\${${classified.key}}`, props, projectVersion);
        if (resolved === null) {
          skipped.push({ name: key, requirement: versionRaw, reason: 'unresolved property' });
        } else {
          const reclassified = classifyVersion(resolved);
          if (reclassified.kind === 'exact') {
            dependencies.push(dep('MAVEN', key, reclassified.version, versionRaw));
          } else if (reclassified.kind === 'range') {
            dependencies.push(rangeDep('MAVEN', key, resolved));
          } else {
            skipped.push({ name: key, requirement: versionRaw, reason: 'unresolved property' });
          }
        }
        break;
      }

      case 'range':
        dependencies.push(rangeDep('MAVEN', key, versionRaw ?? ''));
        break;

      case 'unparseable':
        skipped.push({ name: key, requirement: versionRaw, reason: 'unresolved property' });
        break;

      case 'missing':
        skipped.push({ name: key, requirement: null, reason: 'version managed elsewhere' });
        break;
    }
  }

  return { system: 'MAVEN', dependencies, skipped };
};
